// Custom hook for managing debug state with localStorage persistence
import { useState, useEffect, useCallback } from 'react';

// Debug state interface - centralized definition
export interface DebugState {
  collisionDisabled: boolean;
  speedBoost: boolean;
  showDebugDetails: boolean;
  ignoreBuildingCosts: boolean;
  ignorePowerCosts: boolean;
}

// LocalStorage key for debug settings
const DEBUG_SETTINGS_KEY = 'automation-game-debug-settings';

// Default debug state
const DEFAULT_DEBUG_STATE: DebugState = {
  collisionDisabled: false,
  speedBoost: false,
  showDebugDetails: false,
  ignoreBuildingCosts: false,
  ignorePowerCosts: false,
};

// Load debug settings from localStorage
const loadDebugSettings = (): DebugState => {
  try {
    const saved = localStorage.getItem(DEBUG_SETTINGS_KEY);
    if (saved) {
      const settings = JSON.parse(saved) as DebugState;
      // Ensure all required properties exist with defaults
      return {
        collisionDisabled:
          settings.collisionDisabled ?? DEFAULT_DEBUG_STATE.collisionDisabled,
        speedBoost: settings.speedBoost ?? DEFAULT_DEBUG_STATE.speedBoost,
        showDebugDetails:
          settings.showDebugDetails ?? DEFAULT_DEBUG_STATE.showDebugDetails,
        ignoreBuildingCosts:
          settings.ignoreBuildingCosts ??
          DEFAULT_DEBUG_STATE.ignoreBuildingCosts,
        ignorePowerCosts:
          settings.ignorePowerCosts ?? DEFAULT_DEBUG_STATE.ignorePowerCosts,
      };
    }
  } catch {
    // If loading fails, use defaults
  }
  return DEFAULT_DEBUG_STATE;
};

// Save debug settings to localStorage
const saveDebugSettings = (settings: DebugState): void => {
  try {
    localStorage.setItem(DEBUG_SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    // Ignore localStorage errors
  }
};

// Custom hook for debug state management
export const useDebugState = () => {
  const [debugState, setDebugStateInternal] = useState<DebugState>(() =>
    loadDebugSettings()
  );

  // Save to localStorage whenever state changes
  const setDebugState = useCallback(
    (newState: DebugState | ((prevState: DebugState) => DebugState)) => {
      setDebugStateInternal(prevState => {
        const nextState =
          typeof newState === 'function' ? newState(prevState) : newState;
        saveDebugSettings(nextState);
        return nextState;
      });
    },
    []
  );

  // Individual setters for convenience
  const toggleCollision = useCallback(() => {
    setDebugState(prev => ({
      ...prev,
      collisionDisabled: !prev.collisionDisabled,
    }));
  }, [setDebugState]);

  const toggleSpeedBoost = useCallback(() => {
    setDebugState(prev => ({
      ...prev,
      speedBoost: !prev.speedBoost,
    }));
  }, [setDebugState]);

  const toggleDebugDetails = useCallback(() => {
    setDebugState(prev => ({
      ...prev,
      showDebugDetails: !prev.showDebugDetails,
    }));
  }, [setDebugState]);

  const resetDebugSettings = useCallback(() => {
    setDebugState(DEFAULT_DEBUG_STATE);
  }, [setDebugState]);

  // Load settings on mount (in case localStorage changed externally)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === DEBUG_SETTINGS_KEY) {
        setDebugStateInternal(loadDebugSettings());
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  return {
    debugState,
    setDebugState,
    toggleCollision,
    toggleSpeedBoost,
    toggleDebugDetails,
    resetDebugSettings,
  };
};
